import { useState, useEffect, useCallback } from 'react';
import { adminApi, AdminOrgMember, AdminOrg, AdminRole } from '../../../../services/api/admin';
import { useLoadState } from '../../../../hooks/useLoadState';

interface UseAdminOrgDetailReturn {
  org: AdminOrg | null;
  members: AdminOrgMember[];
  roles: AdminRole[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export const useAdminOrgDetail = (orgId: string): UseAdminOrgDetailReturn => {
  const [org, setOrg] = useState<AdminOrg | null>(null);
  const [members, setMembers] = useState<AdminOrgMember[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  // `reload` läuft nach einer Einladung – dann bleiben die Mitglieder stehen, statt die Seite
  // gegen den Spinner zu tauschen. Der Mandant ist der Schlüssel: Bei einem anderen gehören die
  // Zeilen nicht mehr zusammen.
  const { loading, ladenStarten, ladenBeenden } = useLoadState(orgId);

  const load = useCallback(async () => {
    ladenStarten();
    setError(null);
    try {
      const [orgs, memberData, roleData] = await Promise.all([
        adminApi.getOrganisations(),
        adminApi.getOrgMembers(orgId),
        adminApi.getRoles(),
      ]);
      const found = orgs.find((o) => o.external_id === orgId) ?? null;
      setOrg(found);
      setMembers(memberData);
      setRoles(roleData);
    } catch {
      setError('Daten konnten nicht geladen werden');
    } finally {
      ladenBeenden();
    }
  }, [orgId, ladenStarten, ladenBeenden]);

  useEffect(() => {
    load();
  }, [load]);

  return { org, members, roles, loading, error, reload: load };
};
